import React, { useEffect, useState, ChangeEvent, FormEvent } from 'react';
import {
    ref,
    onValue,
    update,
    remove,
    push,
    set,
    getDatabase, child,
} from 'firebase/database';
import Modal from 'react-modal';
import COLORS, {useBuildingKey} from "../../Helpers/utils";
import {Helmet} from "react-helmet-async";
import {BreadcrumbComponent} from "../../Components/BreadCrumb";
import {ContactModel} from "../../types/protocols";

Modal.setAppElement('#root');

const modalStyles: Modal.Styles = {
    content: {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        transform: 'translate(-50%, -50%)',
        width: '500px',
        maxHeight: '80vh',
        overflowY: 'auto',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
        border: 'none',
    },
    overlay: {
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
    },
};

// Page container
const containerStyle: React.CSSProperties = {
    background: '#f8f9fa',
    minHeight: '100vh',
    padding: '30px',
};

// Header
const headerStyle: React.CSSProperties = {
    textAlign: 'center',
    marginBottom: '30px',
};

// Cards list
const listStyle: React.CSSProperties = {
    listStyleType: 'none',
    padding: 0,
    maxWidth: '600px',
    margin: '0 auto 30px', // Centered horizontally
};

// Individual contact card
const cardStyle: React.CSSProperties = {
    backgroundColor: '#ffffff',
    padding: '15px',
    marginBottom: '10px',
    borderRadius: '8px',
    boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
};

// Contact info container
const infoContainerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'left'
};

// Contact name
const nameStyle: React.CSSProperties = {
    fontSize: '1.2rem',
    fontWeight: 600,
    marginBottom: '5px',
};

// Base button style
const baseButtonStyle: React.CSSProperties = {
    marginRight: '10px',
    padding: '8px 14px',
    color: '#ffffff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.9rem',
};

// Specific button variants
const editButtonStyle: React.CSSProperties = {
    ...baseButtonStyle,
    backgroundColor: COLORS.secondary,
};

const deleteButtonStyle: React.CSSProperties = {
    ...baseButtonStyle,
    backgroundColor: COLORS.redOrange,
    marginRight: 0,
};

const newContactButtonStyle: React.CSSProperties = {
    ...baseButtonStyle,
    backgroundColor: COLORS.primaryDark,
    marginRight: 'auto',
    marginLeft: 'auto',
    marginBottom: '30px',
    display: 'block',
};

const cancelButtonStyle: React.CSSProperties = {
    ...baseButtonStyle,
    backgroundColor: COLORS.secondary,
};

const confirmButtonStyle: React.CSSProperties = {
    ...baseButtonStyle,
    backgroundColor: COLORS.primaryDark,
    marginRight: 0,
};

// Input and error styles
const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px',
    marginTop: '5px',
    boxSizing: 'border-box',
    borderRadius: '4px',
    border: '1px solid #ccc',
};

const errorStyle: React.CSSProperties = {
    border: '1px solid red',
};

// Form label and field wrapper
const fieldWrapperStyle: React.CSSProperties = {
    marginBottom: '10px',
    display: 'flex',
    flexDirection: 'column',
};

// An array to auto-generate fields (excluding 'name' which is handled separately)
const contactFields = [
    { label: 'Roll', name: 'role', type: 'text' },
    { label: 'Företag', name: 'company', type: 'text' },
    { label: 'Telefonnummer', name: 'phoneNumber', type: 'text' },
    { label: 'E-post', name: 'email', type: 'email' },
];

const Contacts: React.FC = () => {
    const [contacts, setContacts] = useState<{ [key: string]: ContactModel }>({});
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

    const [currentContact, setCurrentContact] = useState<ContactModel | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [contactToDelete, setContactToDelete] = useState<string | null>(null);

    const [formData, setFormData] = useState<ContactModel>({
        key: '',
        name: '',
        role: '',
        company: '',
        phoneNumber: '',
        email: '',
        info: '',
    });
    const [formErrors, setFormErrors] = useState<{ name?: string }>({});

    const database = getDatabase();
    const buildingKey = useBuildingKey()
    const contactsRef = ref(database, `buildings/${buildingKey}/contacts`);

    useEffect(() => {
        const unsubscribe = onValue(contactsRef, snapshot => {
            const data = snapshot.val();
            setContacts(data || {});
        });
        return () => unsubscribe();
    }, [database]);

    const openModal = (contact?: ContactModel) => {
        if (contact) {
            // Edit mode
            setIsEditing(true);
            setCurrentContact(contact);
            setFormData(contact);
        } else {
            // Create mode
            setIsEditing(false);
            setCurrentContact(null);
            setFormData({
                key: '',
                name: '',
                role: '',
                company: '',
                phoneNumber: '',
                email: '',
                info: '',
            });
        }
        setFormErrors({});
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
    };

    const openDeleteModal = (key: string) => {
        setContactToDelete(key);
        setIsDeleteModalOpen(true);
    };

    const closeDeleteModal = () => {
        setContactToDelete(null);
        setIsDeleteModalOpen(false);
    };

    const handleFormChange = (
        e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        // Clear name error if user enters a value
        if (name === 'name' && value.trim() !== '') {
            setFormErrors(prev => ({ ...prev, name: undefined }));
        }
    };

    const handleSave = (e: FormEvent) => {
        e.preventDefault();

        // Validate required fields
        if (!formData.name.trim()) {
            setFormErrors({ name: 'Name is required.' });
            return;
        }

        // Edit existing contact
        if (isEditing && currentContact?.key) {
            const contactRef = child(contactsRef, currentContact.key);
            update(contactRef, {
                name: formData.name,
                role: formData.role,
                company: formData.company,
                phoneNumber: formData.phoneNumber,
                email: formData.email,
                info: formData.info,
            }).then();
        } else {
            const newContactRef = push(contactsRef);
            set(newContactRef, { ...formData, key: newContactRef.key }).then();
        }

        closeModal();
    };

    const handleDelete = () => {
        if (contactToDelete) {
            const contactRef = child(contactsRef, contactToDelete);
            remove(contactRef).then();
            closeDeleteModal();
        }
    };

    return (
        <>
            <BreadcrumbComponent
                items={[
                    {text: 'Byggnad', link: `/building/${buildingKey}`},
                    {text: "Kontakter"}
                ]}
            />
            <div style={containerStyle}>
                <Helmet>
                    <title>
                        {`Kontakter`}
                    </title>
                </Helmet>
                <h1 style={headerStyle}>Kontakter</h1>

                <ul style={listStyle}>
                    {Object.keys(contacts).map(key => {
                        const contact = contacts[key];
                        return (
                            <li key={key} style={cardStyle}>
                                <div style={infoContainerStyle}>
                                    <span style={nameStyle}>{contact.name}</span>
                                    {contact.role && <div>Roll: {contact.role}</div>}
                                    {contact.company && <div>Företag: {contact.company}</div>}
                                    {contact.phoneNumber && <div>Telefonnummer: {contact.phoneNumber}</div>}
                                    {contact.email && <div>E-post: {contact.email}</div>}
                                    {contact.info && <div>Info: {contact.info}</div>}
                                </div>
                                <div>
                                    <button style={editButtonStyle} onClick={() => openModal(contact)}>
                                        Redigera
                                    </button>
                                    <button style={deleteButtonStyle} onClick={() => openDeleteModal(key)}>
                                        Radera
                                    </button>
                                </div>
                            </li>
                        );
                    })}
                </ul>

                <button onClick={() => openModal()} style={newContactButtonStyle}>
                    Lägg till kontaktperson
                </button>

                {/* Create/Edit Contact Modal */}
                <Modal
                    isOpen={isModalOpen}
                    onRequestClose={closeModal}
                    contentLabel={isEditing ? 'Redigera kontaktperson' : 'Lägg till kontaktperson'}
                    style={modalStyles}
                >
                    <h2>{isEditing ? 'Redigera kontaktperson' : 'Lägg till kontaktperson'}</h2>
                    <form onSubmit={handleSave}>
                        {/* Name Field (required) */}
                        <div style={fieldWrapperStyle}>
                            <label>Namn *</label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleFormChange}
                                style={{
                                    ...inputStyle,
                                    ...(formErrors.name ? errorStyle : {}),
                                }}
                                required
                            />
                            {formErrors.name && (
                                <div style={{ color: 'red', marginTop: '5px' }}>{formErrors.name}</div>
                            )}
                        </div>

                        {contactFields.map(field => (
                            <div style={fieldWrapperStyle} key={field.name}>
                                <label>{field.label}</label>
                                <input
                                    type={field.type}
                                    name={field.name}
                                    value={formData[field.name as keyof ContactModel] as string}
                                    onChange={handleFormChange}
                                    style={inputStyle}
                                />
                            </div>
                        ))}

                        <div style={fieldWrapperStyle}>
                            <label>Info</label>
                            <textarea
                                name="info"
                                value={formData.info}
                                onChange={handleFormChange}
                                style={inputStyle}
                            />
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                            <button type="button" onClick={closeModal} style={cancelButtonStyle}>
                                Avbryt
                            </button>
                            <button type="submit" style={confirmButtonStyle}>
                                {isEditing ? 'Spara ändringar' : 'Lägg till'}
                            </button>
                        </div>
                    </form>
                </Modal>

                <Modal
                    isOpen={isDeleteModalOpen}
                    onRequestClose={closeDeleteModal}
                    contentLabel="Bekräfta radera"
                    style={modalStyles}
                >
                    <h2>Bekräfta radera</h2>
                    <p>Är du säker på att du vill radera denna kontaktperson?</p>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                        <button onClick={closeDeleteModal} style={cancelButtonStyle}>
                            Avbryt
                        </button>
                        <button onClick={handleDelete} style={deleteButtonStyle}>
                            Radera
                        </button>
                    </div>
                </Modal>
            </div>
        </>
    );
};

export default Contacts;
